/**
 * English strings of the online screens (milestone 6). Same rule as en.ts: `online-it.ts` is typed
 * against this shape, so a missing or extra key is a compile error.
 */
export const onlineEn = {
  online: {
    title: 'Online',
    hub: {
      intro: 'Play with others: join a table, or gather your own crew in a private room.',
      quick: 'Quick match',
      quickHint: 'Any open table in your language. Bots fill the empty seats after a short wait.',
      ranked: 'Ranked',
      rankedHint:
        'Four or five players, no bots, Professional difficulty. Your rating moves with every heist.',
      rankedGuest:
        'Ranked needs a real account: a guest cannot play it. Turn your guest account into one first.',
      create: 'Private room',
      createHint: 'You choose the rules and share a code, a link or a QR with your crew.',
      join: 'Join with a code',
      codeLabel: 'Room code',
      codeHint: 'Six characters, like K7M2QX.',
      joinAction: 'Join',
      friends: 'Friends',
      friendsHint: 'See who is here, invite them to a private room, ask somebody to be a friend.',
      friendsGuest: 'Friends need a real account: a guest cannot have them.',
      friendsOpen: 'Open the friends',
      back: 'Back to the room',
      backHint: 'You are still at a table.',
      resume: 'Go back to your table',
      resumeHint:
        'Your seat is still at the table you were at (a reloaded page or a closed app does not give it up). Go back: from there you can also leave it.',
      signIn: 'Sign in to play online',
      signInBody:
        'Online play needs an account (a guest account will do). The game against bots needs none.',
      signInAction: 'Open the account',
      consentBody:
        'Accept the Terms of use and the Privacy notice in your account before you play online.',
      unavailable: 'Online play is not available in this version',
      unavailableBody: 'This build has no account service or no game server to connect to.',
      connecting: 'Connecting to the table…',
      waking:
        'The server was asleep and is waking up. It can take up to a minute: stay on this page.',
    },
    room: {
      title: 'Room',
      code: 'Room code',
      copy: 'Copy the code',
      copyLink: 'Copy the invite link',
      copied: 'Copied.',
      share: 'Share the invitation',
      shareTitle: 'Il Colpo',
      shareText: 'Join my table at Il Colpo: the code is {{code}}.',
      qr: 'QR code of the invite',
      players: 'Players',
      you: 'you',
      host: 'Host',
      ready: 'Ready',
      notReady: 'Not ready',
      bot: 'Bot',
      dropped: 'Offline',
      kick: 'Remove',
      start: 'Start the heist',
      startWaiting: 'Waiting for everybody to be ready.',
      startNeedsPlayers:
        'A heist needs at least three at the table: turn the bots on or wait for more.',
      imReady: 'I am ready',
      imNotReady: 'Not ready',
      leave: 'Leave the room',
      quickWait: 'The heist starts in {{seconds}} s. Bots will fill the empty seats.',
      quickWaitNow: 'Starting…',
      quickInfo: 'Quick match',
      rankedInfo: 'Ranked',
      rankedNeeded: 'Waiting for players (here: {{here}}): at least four are needed, no bots.',
      rankedWait:
        'The heist starts in {{seconds}} s with whoever is here. A fifth player starts it at once.',
      rankedRules:
        'Professional difficulty, no bots. Leaving a ranked heist costs you a place in the queue.',
      privateInfo: 'Private room',
      rules: 'Rules',
      map: 'Heist',
      difficulty: 'Difficulty',
      infiltrator: 'The Mole',
      size: 'Seats',
      sizeOption: '{{count}} seats',
      bots: 'Bots fill the empty seats',
      botsOn: 'On',
      botsOff: 'Off',
      language: 'Vocabulary',
      draft: 'Pick the roles',
      draftOn: 'On',
      draftOff: 'Off',
      draftHint:
        'When the host starts, the players come up in a random order and each takes a role that is still free. The bots get the rest.',
      rulesReadOnly: 'The host sets the rules.',
      kicked: 'You were removed from the room.',
      closed: 'The room is closed.',
      infiltratorOptions: { never: 'Off', always: 'Always', normal: 'By chance' },
      draftPanel: {
        title: 'Pick your roles',
        yourTurn: 'Your turn: take a role. {{seconds}} s left, then one is picked for you.',
        turnOf: '{{name}} is picking ({{seconds}} s).',
        starting: 'Everybody has a role. Starting…',
        order: 'Order of the picks',
        free: 'Roles still free',
        pick: 'Take {{role}}',
        waiting: 'Waiting',
        picked: 'Picked',
      },
    },
    chat: {
      title: 'Table talk',
      open: 'Chat',
      close: 'Close',
      placeholder: 'Say something to the table',
      send: 'Send',
      empty: 'Nobody has said anything yet.',
      emotes: 'Emotes',
      emoteSent: '{{name}}: {{emote}}',
      emote: {
        hat: 'Tip of the hat',
        thumbsup: 'Thumbs up',
        shh: 'Quiet',
        sweat: 'Nervous',
        cigar: 'Cigar',
        watch: 'Pocket watch',
        glass: 'Raised glass',
        magnifier: 'Magnifying glass',
      },
      forbidden: 'Free text is for players of 14 and over. The quick phrases are always open.',
      you: 'You',
    },
    game: {
      reconnecting: 'Connection lost. Reconnecting…',
      leaveBody: 'A bot will play your seat for good, and the others go on without you.',
      lostTitle: 'The connection is lost',
      lostBody: 'We could not get you back to the table.',
      backToOnline: 'Back to online',
    },
    errors: {
      protocolMismatch: 'This version of the game is too old to play online. Update the app.',
      unauthorized: 'You are not signed in, or your session has ended. Sign in again.',
      roomFull: 'This table is full.',
      notFound: 'There is no room with this code.',
      gameInProgress: 'The heist has already started.',
      alreadyInRoom: 'You are already at another table.',
      queueBanned: 'You left a ranked heist not long ago. You can queue again in {{minutes}} min.',
      accountRequired: 'Ranked play needs a real account, not a guest.',
      featureOff: 'This is switched off for now. Try again later.',
      noAttempts: 'You have used the three attempts of today’s heist. Come back tomorrow.',
      banned: 'Your account is suspended from online play.',
      bannedMinutes: 'Your account is suspended from online play. It ends in {{count}} min.',
      bannedHours: 'Your account is suspended from online play. It ends in about {{count}} h.',
      bannedDays: 'Your account is suspended from online play. It ends in about {{count}} d.',
      muted: 'The chat is closed to you for now. The quick phrases still work.',
      unavailable: 'The service is not available right now. Try again in a moment.',
      updateRequired: 'This version of the game is too old to play online. Update the app.',
      notYourTurn: 'It is not your turn to pick.',
      taken: 'Somebody has taken that role already.',
      notHost: 'Only the host can do this.',
      badRequest: 'That did not work.',
      rateLimited: 'Slow down a little.',
      illegal: 'You cannot do that now.',
      chatForbidden: 'Free text is for players of 14 and over.',
      notEnoughPlayers: 'There are not enough players at the table.',
      consentRequired: 'Accept the Terms of use and the Privacy notice in your account first.',
      connectionLost: 'The connection is lost.',
      unreachable: 'The game server did not answer. Try again in a moment.',
    },
  },
};
